import React from 'react';
import { FaRegMap } from 'react-icons/fa';

const FaRegMapComponent = FaRegMap as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface HealthScoreProps {
  healthScoreData: {
    totalSubscribers: number;
    activeSubscribers: number;
    newThisMonth: number;
    newToday: number;
    unsubscribed: number;
    deltaSinceLastMonthPercent: number;
  };
}

// Helper function to format numbers with abbreviations
const formatNumber = (num: number): string => {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1) + 'B';
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1) + 'M';
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(0) + 'k';
  }
  return num.toString();
};

const HealthScore: React.FC<HealthScoreProps> = ({ healthScoreData }) => {
  const growthPercent = healthScoreData.deltaSinceLastMonthPercent;

  // Define the cards directly (no stats array from parent)
  const cards = [
    {
      label: 'Active',
      value: formatNumber(healthScoreData.activeSubscribers),
      color: '#7A52CC',
      iconcolor: 'text-purple-500',
    },
    {
      label: 'New this Month',
      value: formatNumber(healthScoreData.newThisMonth),
      color: '#52CCB8',
      iconcolor: 'text-teal-500',
    },
    {
      label: 'New today',
      value: formatNumber(healthScoreData.newToday),
      color: '#CCB852',
      iconcolor: 'text-yellow-500',
    },
    {
      label: 'Unsubscribed',
      value: formatNumber(healthScoreData.unsubscribed),
      color: '#CC7A52',
      iconcolor: 'text-red-500',
    },
  ];

  return (
    <div className='bg-white p-4 rounded-lg shadow-sm border'>
      {/* Header */}
      <h3
        className='text-lg font-semibold mb-5'
        style={{ color: '#0D121A', fontSize: '20px' }}
      >
        Email Health Score
      </h3>

      {/* Total Subscribers */}
      <p style={{ color: '#657584', fontSize: '12px' }}>
        Total Subscribers
      </p>
      <p
        className='text-3xl font-bold -mt-2'
        style={{ color: '#3F5265', fontSize: '20px' }}
      >
        {formatNumber(healthScoreData.totalSubscribers)}
      </p>

      <p
        className='text-green-600 text-sm'
        style={{ fontSize: '12px' }}
      >
        ↑ {growthPercent}%{' '}
        <span style={{ color: '#657584' }}>Since Last Month</span>
      </p>

      {/* Colored Stats Cards - Original Design */}
      <div className='mt-8 space-y-3'>
        {cards.map((item, index) => (
          <div
            key={index}
            className='text-white rounded-3xl p-4 flex justify-between items-center'
            style={{
              height: index === cards.length - 1 ? '70px' : '100px',
              alignItems: 'start',
              marginTop: index > 0 ? '-15%' : '0px',
              backgroundColor: item.color,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span
                className={`flex items-center justify-center mr-2 bg-white rounded-md ${item.iconcolor}`}
                style={{
                  width: '25px',
                  height: '25px',
                  flexShrink: 0,
                }}
              >
                <FaRegMapComponent size={14} />
              </span>
              <span className='font-medium' style={{ fontSize: '12px' }}>
                {item.label}
              </span>
            </div>

            <span className='font-normal' style={{ fontSize: '20px' }}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HealthScore;